import { useState, useEffect } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SignaturePad } from "./SignaturePad";
import AuthImage from "./AuthImage";
import { toast } from "sonner";

import DateInput from "./DateInput";
/**
 * Reusable dialog for filling and submitting a Job Form template on a given job.
 * Usable by admins, suppliers, and in-house operatives.
 *
 * Props:
 *   jobId   - string, the job id
 *   form    - { id, title, description, fields: [...] } | null
 *   open    - boolean
 *   onClose - () => void
 *   onSubmitted - () => void (called after successful POST)
 */
export default function JobFormFillDialog({ jobId, form, open, onClose, onSubmitted }) {
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && form) {
      const draft = localStorage.getItem(`draft-${jobId}-${form.id}`);
      if (draft) {
        try { setResponses(JSON.parse(draft)); toast.info("Draft restored"); return; } catch { /* noop */ }
      }
      setResponses({});
    }
  }, [open, form, jobId]);

  if (!form) return null;

  const fields = form.fields || [];
  const filled = fields.filter(f => {
    const v = responses[f.id];
    return v !== undefined && v !== "" && v !== null && v !== false && !(Array.isArray(v) && v.length === 0);
  }).length;
  const pct = fields.length ? Math.round((filled / fields.length) * 100) : 0;

  const uploadPhoto = async (fieldId, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", "job-form-photos");
        const { data } = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setResponses(prev => ({ ...prev, [fieldId]: [...(prev[fieldId] || []), { name: file.name, url: data.url, type: file.type }] }));
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
    e.target.value = "";
  };

  const submit = async () => {
    for (const f of fields) {
      if (f.required) {
        const v = responses[f.id];
        if (v === undefined || v === "" || v === null || (Array.isArray(v) && v.length === 0)) {
          toast.error(`"${f.label}" is required`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      await API.post(`/jobs/${jobId}/form-submissions`, { form_id: form.id, responses });
      localStorage.removeItem(`draft-${jobId}-${form.id}`);
      toast.success("Form submitted");
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="form-fill-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">{form.title || "Fill Form"}</DialogTitle>
          <DialogDescription>{form.description || "Complete all required fields and submit."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div data-testid="form-progress">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-muted-foreground">{filled} of {fields.length} fields completed</span>
              <span className="text-[10px] font-semibold text-primary">{pct}%</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
          </div>
          {fields.map((field) => (
            <div key={field.id} data-testid={`fill-field-${field.id}`}>
              <Label className="text-sm">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </Label>
              {field.type === "text" && (
                <Input value={responses[field.id] || ""} onChange={e => setResponses(prev => ({ ...prev, [field.id]: e.target.value }))} placeholder={field.placeholder} className="mt-1" />
              )}
              {field.type === "textarea" && (
                <Textarea value={responses[field.id] || ""} onChange={e => setResponses(prev => ({ ...prev, [field.id]: e.target.value }))} placeholder={field.placeholder} rows={3} className="mt-1" />
              )}
              {field.type === "number" && (
                <Input type="number" value={responses[field.id] || ""} onChange={e => setResponses(prev => ({ ...prev, [field.id]: e.target.value }))} placeholder={field.placeholder} className="mt-1" />
              )}
              {field.type === "date" && (
                <DateInput value={responses[field.id] || ""} onChange={e => setResponses(prev => ({ ...prev, [field.id]: e.target.value }))} className="mt-1" />
              )}
              {field.type === "checkbox" && (
                <div className="flex items-center gap-2 mt-1">
                  <input type="checkbox" checked={responses[field.id] || false} onChange={e => setResponses(prev => ({ ...prev, [field.id]: e.target.checked }))} className="rounded" id={`cb-${field.id}`} />
                  <label htmlFor={`cb-${field.id}`} className="text-sm text-muted-foreground">Yes</label>
                </div>
              )}
              {field.type === "dropdown" && (
                <Select value={responses[field.id] || ""} onValueChange={v => setResponses(prev => ({ ...prev, [field.id]: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {(field.options || []).filter(Boolean).map((opt, i) => <SelectItem key={i} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {field.type === "photo" && (
                <div className="mt-1">
                  <input type="file" accept="image/*" multiple onChange={e => uploadPhoto(field.id, e)} className="block w-full text-xs text-muted-foreground file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer" />
                  <p className="text-[9px] text-muted-foreground mt-0.5">Select multiple photos at once or add more individually</p>
                  {(responses[field.id] || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {responses[field.id].map((p, i) => (
                        <div key={i} className="relative h-14 w-14 rounded-md overflow-hidden border border-border">
                          <AuthImage path={p.url} alt={p.name} className="w-full h-full object-cover" />
                          <button type="button" className="absolute top-0 right-0 bg-destructive text-white rounded-bl text-[8px] px-1" onClick={() => setResponses(prev => ({ ...prev, [field.id]: prev[field.id].filter((_, idx) => idx !== i) }))}>&times;</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {field.type === "signature" && (
                <SignaturePad value={responses[field.id] || ""} onChange={v => setResponses(prev => ({ ...prev, [field.id]: v }))} height={120} className="mt-1" />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" className="mr-auto text-xs" onClick={() => { localStorage.setItem(`draft-${jobId}-${form.id}`, JSON.stringify(responses)); toast.success("Draft saved locally"); }} data-testid="save-draft-btn">Save Draft</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="submit-form-btn">{submitting ? "Submitting..." : "Submit Form"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
